import { NAVIGATION_CONTAINER_PREVIEW } from '@unifyapps/carbon/no-code/const';
import { INTERFACE_THEME, UA_THEME_VARS } from '@unifyapps/style/themes/consts';
import type { AppThemeConfigContextValue } from '../context/AppThemeConfigContext';
import { getVariablesStringFromTheme } from './getVariablesStringFromTheme';

export const stringifyThemeForPlatform = (style: AppThemeConfigContextValue['theme'] | undefined) =>
  style
    ? `
  .${UA_THEME_VARS},
  [data-color-scheme='light'] {
    ${getVariablesStringFromTheme(style)}
  }
`
    : '';

export const stringifyThemeForMatrix = (
  style: AppThemeConfigContextValue['theme'] | undefined,
  theme: 'light' | 'dark' = 'light',
) => {
  if (style) {
    if (theme === 'dark') {
      // ${INTERFACE_THEME}[data-color-scheme='dark'] is used for the root element
      // .${INTERFACE_THEME} [data-color-scheme='dark'] is used for the child elements
      return `
        .${INTERFACE_THEME}[data-color-scheme='dark'],
        .${INTERFACE_THEME} [data-color-scheme='dark'] {
          ${getVariablesStringFromTheme(style)}
        }
      `;
    }
    return `
      .${INTERFACE_THEME},
      .${INTERFACE_THEME} [data-color-scheme='light'] {
        ${getVariablesStringFromTheme(style)}
      }
    `;
  }

  return '';
};

/**
 * matches one top-level rule opening at the start of a line.
 * captures the dark prefix (if any) and the rest of the selector.
 *   group 1 — `[data-color-scheme="dark"] ` if present, empty otherwise
 *   group 2 — the class-prefixed selector (`.ua-button`)
 * `m` for per-line `^`; `g` to rewrite every rule.
 */
const COMPILED_OUTPUT_RULE_OPENING_REGEX = /^(\[data-color-scheme="dark"\]\s+)?(\.[^{]+)\{/gm;

/**
 * root selectors whose component renders the scope class (e.g. `.interface-theme`)
 * on the SAME element as the rule subject — not on an ancestor. Toast lives in a
 * sonner portal at body root with `interface-theme`, `ua-variables`, `ua-toast`,
 * and `data-color-scheme` all stacked on one `<div>`. Descendant scoping fails
 * for that DOM shape; concat scoping is required.
 */
const SELF_SCOPED_ROOTS = new Set(['.ua-toast']);

function isSelfScoped(selector: string): boolean {
  for (const root of SELF_SCOPED_ROOTS) {
    if (selector.startsWith(root)) return true;
  }
  return false;
}

/**
 * inserts an attribute selector after the FIRST class + attribute-selector run,
 * before any pseudo-class / pseudo-element / combinator / descendant. needed so
 * dark concat-scoping for self-scoped rules emits the attribute at the correct
 * spot inside compound selectors:
 *   `.ua-toast[data-color='neutral']:hover` + `[data-color-scheme="dark"]`
 *   → `.ua-toast[data-color='neutral'][data-color-scheme="dark"]:hover`
 *   `.ua-toast .ua-toast-header` + `[data-color-scheme="dark"]`
 *   → `.ua-toast[data-color-scheme="dark"] .ua-toast-header`
 */
function injectSelfAttr(selector: string, attr: string): string {
  return selector.replace(/^([.\w-]+(?:\[[^\]]*\])*)/, `$1${attr}`);
}

interface ScopedRuleArgs {
  selector: string;
  scope: string;
  isDark: boolean;
}

/**
 * concat scoping — rule subject IS the scope element (e.g. toast portal where
 * `.interface-theme` and `.ua-toast` stack on one DOM node). Single emit
 * because the attribute can only live on the rule subject for this DOM shape,
 * so the descendant dual-emit form would never match.
 *
 * @example light
 * Given scope `.interface-theme`, input:
 * ```
 * .ua-toast[data-color='neutral']:hover { color: red; }
 * ```
 * output:
 * ```
 * .interface-theme.ua-toast[data-color='neutral']:hover { color: red; }
 * ```
 *
 * @example dark
 * Given scope `.interface-theme`, input:
 * ```
 * [data-color-scheme="dark"] .ua-toast[data-color='neutral']:hover { color: red; }
 * ```
 * output:
 * ```
 * .interface-theme.ua-toast[data-color='neutral'][data-color-scheme="dark"]:hover { color: red; }
 * ```
 */
function buildSelfScopedRule({ selector, scope, isDark }: ScopedRuleArgs): string {
  if (isDark) {
    const withAttr = injectSelfAttr(selector, '[data-color-scheme="dark"]');
    return `${scope}${withAttr} {`;
  }
  return `${scope}${selector} {`;
}

/**
 * descendant scoping — rule subject lives INSIDE the scope element (buttons,
 * inputs, etc. nested under `.interface-theme`). Dual-emit on dark mirrors
 * `stringifyThemeForMatrix` so rules fire whether the color-scheme attribute
 * lives on the scope element or on a descendant.
 *
 * @example light
 * Given scope `.interface-theme`, input:
 * ```
 * .ua-button { color: red; }
 * ```
 * output:
 * ```
 * .interface-theme .ua-button { color: red; }
 * ```
 *
 * @example dark
 * Given scope `.interface-theme`, input:
 * ```
 * [data-color-scheme="dark"] .ua-button { color: red; }
 * ```
 * output:
 * ```
 * .interface-theme[data-color-scheme="dark"] .ua-button,
 * .interface-theme [data-color-scheme="dark"] .ua-button { color: red; }
 * ```
 */
function buildDescendantScopedRule({ selector, scope, isDark }: ScopedRuleArgs): string {
  if (isDark) {
    return `${scope}[data-color-scheme="dark"] ${selector}, ${scope} [data-color-scheme="dark"] ${selector} {`;
  }
  return `${scope} ${selector} {`;
}

/**
 * prefixes each CSS rule selector in a compiledOutput string with a scope
 * selector, dispatching to self vs descendant scoping based on whether the
 * rule subject carries `.interface-theme` itself.
 */
function prefixComponentSelectors(compiledOutput: string, scopeSelector: string): string {
  if (!compiledOutput) return '';
  return compiledOutput.replace(
    COMPILED_OUTPUT_RULE_OPENING_REGEX,
    (_match, darkPrefix: string | undefined, selector: string) => {
      const trimmed = selector.trim();
      const args: ScopedRuleArgs = {
        selector: trimmed,
        scope: scopeSelector,
        isDark: Boolean(darkPrefix),
      };
      return isSelfScoped(trimmed)
        ? buildSelfScopedRule(args)
        : buildDescendantScopedRule(args);
    },
  );
}

/**
 * scopes component style rules under .interface-theme,
 * matching the same boundary used by stringifyThemeForMatrix for CSS variables.
 * use in matrix runtime and builder preview.
 */
export const scopeComponentStylesForMatrix = (compiledOutput: string): string =>
  prefixComponentSelectors(compiledOutput, `.${INTERFACE_THEME}`);

/**
 * scopes component style rules under .ua-variables,
 * matching the same boundary used by stringifyThemeForPlatform for CSS variables.
 * use in platform theme builder preview.
 */
export const scopeComponentStylesForPlatform = (compiledOutput: string): string =>
  prefixComponentSelectors(compiledOutput, `.${UA_THEME_VARS}`);

export const getCssVarValue = (cssVar: string) => {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const element = document.querySelector(`.${UA_THEME_VARS}`);
  if (element) {
    const elementStyles = getComputedStyle(element);
    return elementStyles.getPropertyValue(cssVar).trim();
  }

  return undefined;
};

export const getInterfaceCssVarElementStyles = () => {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const element = document.querySelector(`.${NAVIGATION_CONTAINER_PREVIEW}`);

  if (element) {
    const elementStyles = getComputedStyle(element);
    return elementStyles;
  }

  return undefined;
};
